import React, { useEffect, useState } from 'react';
import axios from 'axios';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts';
import worldGeoJson from '../../assets/world.json';

echarts.registerMap('world', worldGeoJson);

const SpeciesDetail = (props) => {
  const { id } = props.match.params;

  const [option, setOption] = useState({});
  const [speciesInfo, setSpeciesInfo] = useState(null);
  const [targetSetName, setTargetSetName] = useState('');
  const [setCount, setSetCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!id) { setError("No speciesId"); return; }

    axios
      .get(`/api/habitatUpSet/${id}`, { params: { topN: 50 } })
      .then((res) => {
        const { sets, combinations, targetSetName: tsn } = res.data;
        setTargetSetName(tsn || '');
        setSetCount(sets.length);

        const setNames = sets.map((s) => s.name);
        const N = setNames.length;
        const spHabitats = new Set(tsn ? tsn.split(',') : []);
        const sizes = sets.map((s) => s.cardinality);
        const leftColors = sizes.map((_, i) => spHabitats.has(setNames[i]) ? '#EA8379' : '#41AB8D');
        const comboNames = (combinations || []).map((c) => c.name);
        const topColors = comboNames.map((n) => n === tsn ? '#EA8379' : '#5470C6');
        const comboLabels = comboNames.map((n) => n.substring(0, 30));
        const comboSizes = (combinations || []).map((c) => c.cardinality);

        const matrixData = [];
        (combinations || []).forEach((combo, ci) => {
          combo.setNames.forEach((nm) => {
            const si = setNames.indexOf(nm);
            if (si >= 0) matrixData.push({ value: [ci, si], symbolSize: 8 });
          });
        });

        const lineSeries = (combinations || []).map((combo, ci) => {
          const pts = combo.setNames.map((nm) => setNames.indexOf(nm)).filter((v) => v >= 0).sort((a, b) => a - b);
          return { type: 'line', xAxisIndex: 2, yAxisIndex: 2, data: pts.map((v) => [ci, v]), lineStyle: { color: '#666', width: 2 }, symbol: 'none', silent: true, z: 1 };
        });

        const rowH = 14;
        const totalH = Math.max(1200, N * rowH + 300);

        setOption({
          tooltip: {
            trigger: 'item',
            confine: true,
            extraCssText: 'max-width:400px; white-space:normal; word-break:break-all;',
            formatter: (p) => {
              if (p.seriesType === 'bar') {
                if (p.seriesIndex === 0) return '<b>' + p.name + '</b><br/>Sample(run) count: ' + p.value;
                if (p.seriesIndex === 1) return '<b>Combination ' + (p.dataIndex + 1) + '</b><br/>Species count: ' + p.value + '<br/><br/>' + comboNames[p.dataIndex].replace(/,/g, ', ');
              }
              if (p.seriesType === 'scatter') {
                const ci = p.value[0], si = p.value[1];
                return '<b>Combination ' + (ci + 1) + '</b><br/>Includes: ' + setNames[si] + '<br/><br/>' + comboNames[ci].replace(/,/g, ', ');
              }
              return '';
            },
          },
          grid: [
            { left: 30,  top: 200, width: 160, height: totalH - 250 },
            { left: 360, top: 60,  width: 'auto', right: 20, height: 110 },
            { left: 360, top: 200, width: 'auto', right: 20, height: totalH - 250 },
          ],
          xAxis: [
            { gridIndex: 0, type: 'value', inverse: true, name: 'Sample(run) count', nameLocation: 'middle', nameGap: 25, axisLabel: { fontSize: 10 } },
            { gridIndex: 1, type: 'category', data: comboLabels, axisLabel: { show: false }, axisTick: { show: false }, splitLine: { show: false } },
            { gridIndex: 2, type: 'category', data: comboLabels, position: 'top', axisLabel: { show: false }, axisTick: { show: false }, splitLine: { show: false } },
          ],
          yAxis: [
            { gridIndex: 0, type: 'category', data: setNames, inverse: true, position: 'right', axisLabel: { fontSize: 9, width: 160, overflow: 'truncate', ellipsis: '...' }, axisTick: { show: false }, splitLine: { show: false } },
            { gridIndex: 1, type: 'value', name: 'Species count', nameLocation: 'end', axisLabel: { fontSize: 10 }, splitLine: { show: false } },
            { gridIndex: 2, type: 'category', data: setNames, inverse: true, axisLabel: { show: false }, axisTick: { show: false }, splitLine: { show: false } },
          ],
          series: [
            { name: 'Set size', type: 'bar', xAxisIndex: 0, yAxisIndex: 0, data: sizes.map((v, i) => ({ value: v, itemStyle: { color: leftColors[i] } })), label: { show: true, position: 'left', fontSize: 9, color: '#333' }, barMaxWidth: 12 },
            { name: 'Intersection', type: 'bar', xAxisIndex: 1, yAxisIndex: 1, data: comboSizes.map((v, i) => ({ value: v, itemStyle: { color: topColors[i] } })), label: { show: true, position: 'top', fontSize: 9, color: '#333' }, barMaxWidth: 30 },
            ...lineSeries,
            { name: 'Matrix', type: 'scatter', xAxisIndex: 2, yAxisIndex: 2, data: matrixData, symbol: 'circle', symbolSize: 10, itemStyle: { color: '#333' }, z: 2 },
          ],
        });

        setLoading(false);
      })
      .catch((err) => { setError("Failed to fetch data."); setLoading(false); });

    axios.get(`/api/speciesInfo/${id}`).then((r) => setSpeciesInfo(r.data)).catch((e) => console.error(e));
  }, [id]);

  if (error) return <p>{error}</p>;
  if (loading) return <p>Loading...</p>;

  return (
    <div style={{ padding: "20px" }}>
      {speciesInfo && (
        <div style={{ marginBottom: "20px" }}>
          <h1>Species: {speciesInfo.name}</h1>
          <p>Found in <strong>{speciesInfo.runs}</strong> runs.</p>
          <p><a href={speciesInfo.ncbiLink} target="_blank" rel="noopener noreferrer">NCBI</a> | <a href={speciesInfo.enaLink} target="_blank" rel="noopener noreferrer">ENA</a></p>
          {targetSetName && <h2 style={{ color:'#333', fontSize:'1.5em', wordBreak:'normal' }}>Habitats where this species was found (Total: {targetSetName.split(',').length} habitats)</h2>}
          {targetSetName && <p style={{ color:'#333', fontSize:13, wordBreak:'normal' }}>{targetSetName}</p>}
        </div>
      )}
      <h2>UpSet Plot</h2>
      <p style={{ fontSize:13, color:'#666',  lineHeight:1.6, marginBottom:8 }}>
      The UpSet plot visualizes the co-occurrence patterns of samples across {setCount} habitats.
      <b> Left bars:</b> number of samples found in each habitat.
      <b> Top bars:</b> number of distinct species belonging to each habitat combination, sorted in descending order of species count.
      <b> Dot matrix:</b> dots connected by vertical lines indicate which habitats belong to each combination.
      A single dot (no line) represents species unique to one habitat;
      linked dots represent species shared across multiple habitats.
      Orange bars mark the habitat containing the current species (left) and its matching habitat combination (top).
      This plot displays a contextual window of up to 50 habitat combinations centered on the current species’ habitat set, ordered by the number of species per combination.
      </p>
      <ReactECharts option={option} style={{ height: (option.grid ? option.grid[0].height + 400 : 1600), width: '100%' }} notMerge />
      <div style={{ height:'50px' }}></div>
    </div>
  );
};

export default SpeciesDetail;
